import * as React from "react";
import toast from "react-hot-toast";
import { NotificationProps, NotificationPosition, NotificationTheme } from "./types";

/* ─────────────────────────────────────────────────────────────────────────────
   Theme + icon definitions
   Colours follow the shared UI kit tokens (styles/components/shared/ui.scss).
───────────────────────────────────────────────────────────────────────────── */

interface ThemeSpec { accent: string; bg: string; fg: string; }

const THEMES: { [K in NotificationTheme]: ThemeSpec } = {
    success: { accent: "#16a34a", bg: "#f0fdf4", fg: "#14532d" },
    danger: { accent: "#dc2626", bg: "#fef2f2", fg: "#7f1d1d" },
    warning: { accent: "#d97706", bg: "#fffbeb", fg: "#78350f" },
    primary: { accent: "#2563eb", bg: "#eff6ff", fg: "#1e3a8a" },
    purple: { accent: "#7c3aed", bg: "#f5f3ff", fg: "#4c1d95" },
    inverted: { accent: "#0f172a", bg: "#f8fafc", fg: "#0f172a" },
};

const ICONS: { [K in NotificationTheme]: React.ReactNode } = {
    success: (
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M4.5 10.5l3.5 3.5 7.5-7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    danger: (
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M10 5.5v5.5M10 14.2v.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.6" />
        </svg>
    ),
    warning: (
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M10 3.5l7 12.5H3l7-12.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            <path d="M10 8.5v3M10 13.8v.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    ),
    primary: (
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M10 9v5M10 6.2v.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    ),
    purple: (
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M10 9v5M10 6.2v.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    ),
    inverted: (
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M10 9v5M10 6.2v.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
    ),
};

/** Map the legacy position values onto react-hot-toast's positions. */
const POSITIONS: { [key: string]: any } = {
    "top": "top-center",
    "bottom": "bottom-center",
    "top-left": "top-left",
    "top-right": "top-right",
    "bottom-left": "bottom-left",
    "bottom-right": "bottom-right",
};

const DEFAULT_DURATION = 4000;

/* ─────────────────────────────────────────────────────────────────────────────
   Toast body
───────────────────────────────────────────────────────────────────────────── */

interface ToastBodyProps {
    id: string;
    title?: string;
    message?: string;
    theme: NotificationTheme;
    dismissable: boolean;
    onDismiss?: () => void;
    onClick?: (event?: React.MouseEvent<HTMLDivElement>) => void;
    className?: string;
    visible: boolean;
}

const ToastBody: React.FC<ToastBodyProps> = ({
    id, title, message, theme, dismissable, onDismiss, onClick, className = "", visible,
}) => {
    const spec = THEMES[theme] || THEMES.primary;

    const close = () => {
        toast.dismiss(id);
        onDismiss && onDismiss();
    };

    return (
        <div
            className={`app-toast ${visible ? "app-toast--in" : "app-toast--out"} ${className}`}
            style={{ background: spec.bg, borderLeft: `4px solid ${spec.accent}`, color: spec.fg }}
            onClick={onClick as any}
            role="status"
            aria-live="polite"
        >
            <span className="app-toast__icon" style={{ color: spec.accent }}>
                {ICONS[theme] || ICONS.primary}
            </span>

            <div className="app-toast__content">
                {title ? <div className="app-toast__title">{title}</div> : null}
                {message ? <div className="app-toast__message">{message}</div> : null}
            </div>

            {dismissable && (
                <button
                    type="button"
                    className="app-toast__close"
                    aria-label="Dismiss notification"
                    onClick={(e) => { e.stopPropagation(); close(); }}
                >
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                        <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                </button>
            )}
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────────────────────
   Core: fire a toast from a NotificationProps object
───────────────────────────────────────────────────────────────────────────── */

export function showNotification(props: NotificationProps): string {
    const {
        title, message, theme = "primary", persist, dismissTimeout,
        position = "top-right", dismissable = true, className, onClick, onDismiss,
    } = props;

    // Nothing to show — don't emit an empty toast.
    if (!title && !message) return "";

    return toast.custom(
        (t) => (
            <ToastBody
                id={t.id}
                visible={t.visible}
                title={title}
                message={message}
                theme={theme}
                dismissable={dismissable !== false}
                onDismiss={onDismiss}
                onClick={onClick}
                className={className}
            />
        ),
        {
            duration: persist ? Infinity : (dismissTimeout || DEFAULT_DURATION),
            position: POSITIONS[position as NotificationPosition] || "top-right",
        }
    );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Convenience API for new code
───────────────────────────────────────────────────────────────────────────── */

const build = (theme: NotificationTheme) =>
    (message: string, title?: string, options: Partial<NotificationProps> = {}) =>
        showNotification({ theme, message, title, toggle: true, onDismiss: () => { }, ...options });

export const notify = {
    success: build("success"),
    error: build("danger"),
    danger: build("danger"),
    warning: build("warning"),
    info: build("primary"),
    /** Dismiss one toast by id, or all toasts when called with no argument. */
    dismiss: (id?: string) => toast.dismiss(id),
    /** Escape hatch to the underlying react-hot-toast instance. */
    raw: toast,
};

export default notify;
